import { ChoiceChip } from '@/components/explore/CreatorFields'
import { cn } from '@/lib/utils'
import type { OperatingHours } from './types'

/** Domingo é 0, como no `Date#getDay` e como a escola já grava. */
const DAYS = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
]

const DEFAULT_MORNING = { start: '08:00', end: '12:00' }
const DEFAULT_AFTERNOON = { start: '14:00', end: '18:00' }

/**
 * Passo 5 — quando o negócio atende.
 *
 * O shape é o MESMO que a escola já grava (`{days, morning, afternoon}`), para
 * a página pública desenhar os dois com um componente só. Manhã e tarde em vez
 * de um intervalo corrido porque é assim que quase todo negócio de praia
 * funciona — e porque era o recorte que o admin da escola já usava.
 *
 * Tudo opcional: quem não quer publicar horário deixa em branco e a seção
 * simplesmente não aparece na página.
 */
export default function StepHours({
  hours,
  onChange,
}: {
  hours: OperatingHours
  onChange: (next: OperatingHours) => void
}) {
  const toggleDay = (day: number) => {
    const days = hours.days.includes(day)
      ? hours.days.filter((d) => d !== day)
      : [...hours.days, day].sort((a, b) => a - b)
    onChange({ ...hours, days })
  }

  return (
    <div className="space-y-6">
      <p className="max-w-2xl text-sm text-gray-600">
        Quem está decidindo se chama você quer saber se hoje tem. Deixe em
        branco se prefere combinar tudo pelo WhatsApp.
      </p>

      <div>
        <p className="text-sm font-semibold text-gray-800">
          Quais dias você atende?
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {DAYS.map((day) => (
            <ChoiceChip
              key={day.value}
              label={day.label}
              selected={hours.days.includes(day.value)}
              onClick={() => toggleDay(day.value)}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() =>
            onChange({
              ...hours,
              days:
                hours.days.length === DAYS.length
                  ? []
                  : DAYS.map((d) => d.value),
            })
          }
          className="mt-2 text-xs font-semibold text-primary hover:underline"
        >
          {hours.days.length === DAYS.length
            ? 'Limpar seleção'
            : 'Todos os dias'}
        </button>
      </div>

      <PeriodRow
        label="Manhã"
        range={hours.morning}
        onToggle={(on) =>
          onChange({ ...hours, morning: on ? DEFAULT_MORNING : null })
        }
        onChange={(range) => onChange({ ...hours, morning: range })}
      />

      <PeriodRow
        label="Tarde"
        range={hours.afternoon}
        onToggle={(on) =>
          onChange({ ...hours, afternoon: on ? DEFAULT_AFTERNOON : null })
        }
        onChange={(range) => onChange({ ...hours, afternoon: range })}
      />
    </div>
  )
}

function PeriodRow({
  label,
  range,
  onToggle,
  onChange,
}: {
  label: string
  range: { start: string; end: string } | null
  onToggle: (on: boolean) => void
  onChange: (range: { start: string; end: string }) => void
}) {
  const ligado = range != null

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <label className="flex cursor-pointer items-center gap-2.5">
        <input
          type="checkbox"
          checked={ligado}
          onChange={(e) => onToggle(e.target.checked)}
          className="size-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
        <span className="text-sm font-semibold text-gray-800">{label}</span>
      </label>

      <div
        className={cn(
          'mt-3 flex items-center gap-2',
          !ligado && 'pointer-events-none opacity-40',
        )}
      >
        <TimeInput
          label={`${label}: início`}
          value={range?.start ?? ''}
          onChange={(start) =>
            onChange({ start, end: range?.end ?? DEFAULT_MORNING.end })
          }
          disabled={!ligado}
        />
        <span className="text-sm text-gray-400">até</span>
        <TimeInput
          label={`${label}: fim`}
          value={range?.end ?? ''}
          onChange={(end) =>
            onChange({ start: range?.start ?? DEFAULT_MORNING.start, end })
          }
          disabled={!ligado}
        />
      </div>
    </div>
  )
}

function TimeInput({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  disabled: boolean
}) {
  return (
    <input
      type="time"
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none transition-colors focus:border-gray-400"
    />
  )
}
